import './Index.css'

import { Link, useLocation, useParams } from "react-router"
import { Box, Button, Divider } from "@mui/material"
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { MuiMarkdown } from 'mui-markdown';
import { getViewPath, getViewRouteFromPathname } from './viewRoute';

import { useEffect, useState, useSyncExternalStore } from 'react';
import { readBearerToken, subscribeToBearerToken } from './grampsAuth';
import { apiGet } from './api/grampsApi';
import { getMathMarkdownOptions } from './components/markdownMath';
import { usePublishedStories } from './hooks/useStories';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';

type GrampsNoteApiItem = {
    handle: string;
    type: string;
    text: { string: string };
}

type GrampsNotesApiResponse = GrampsNoteApiItem[]

function noteToText(note: GrampsNoteApiItem): string {
    return note.text.string.trim();
}

async function fetchIndexNotesMarkdown(token: string | null): Promise<string> {
    if (!token) return '';

    const rules = JSON.stringify({
        rules: [{ name: 'HasTag', values: ['Index'] }],
    });

    const payload = await apiGet('/api/notes/', {
        token,
        query: {
            rules,
            locale: 'fr',
            profile: 'all',
            extend: 'all',
            sort: ['change'],
            pagesize: 200,
            page: 1,
        },
    }) as unknown as GrampsNotesApiResponse | null;

    const notes = (payload ?? [])
        .map(noteToText)
        .filter((text) => text.length > 0);

    return notes.join('\n\n---\n\n');
}

export function Index() {
    const [md, setMd] = useState('')
    const location = useLocation()
    const { treeName } = useParams<{ treeName?: string }>()
    const currentRoute = getViewRouteFromPathname(location.pathname)
    const explorerPath = getViewPath('ascendency', undefined, undefined, treeName ?? currentRoute.treeName)
    // Reactive so a token obtained after a 401 (silent refresh or the login dialog)
    // re-fires this effect -- this fetch isn't react-query-managed, so it wouldn't
    // otherwise pick up on the app-wide `invalidateQueries()` that runs on login.
    const token = useSyncExternalStore(subscribeToBearerToken, () => readBearerToken())
    const activeTree = treeName ?? currentRoute.treeName
    // Offered only once there is something to read.
    const stories = usePublishedStories(activeTree)

    useEffect(() => {
        fetchIndexNotesMarkdown(token)
            .then((text) => setMd(text))
            .catch(() => setMd(''))
    }, [token])

    return <Box className="index">
        <Divider style={{ marginTop: "20px", marginBottom: "20px"}}></Divider>

        Pour commencer l'exploration, cliquez sur &nbsp;
        <Button
            component={Link}
            to={explorerPath}
            variant="contained"
            startIcon={<PlayArrowRoundedIcon />}
            sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: '999px',
                px: 2.5,
                py: 1,
                background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
                boxShadow: '0 8px 20px rgba(239, 68, 68, 0.30)',
                '&:hover': {
                    background: 'linear-gradient(135deg, #d97706 0%, #dc2626 100%)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 10px 22px rgba(220, 38, 38, 0.38)',
                },
            }}
        >
            explorer la généalogie
        </Button>
        {activeTree && (stories.data?.length ?? 0) > 0 && <>
            &nbsp;ou&nbsp;
            <Button
                component={Link}
                to={`/${activeTree}/recits`}
                variant="outlined"
                startIcon={<MenuBookRoundedIcon />}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '999px', px: 2.5, py: 1 }}
            >
                lire les récits
            </Button>
        </>}

        <Divider style={{ marginTop: "20px", marginBottom: "20px"}}></Divider>

        <MuiMarkdown options={getMathMarkdownOptions()}>{md}</MuiMarkdown>

    </Box>
}